import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { cloneDeep, isNil, isUndefined, sortBy } from 'lodash-es'
import { PaymentSettingContract } from 'models'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { showErrorMessage } from 'store/actions/ui.action'
import { useAppSelector } from 'store/useStoreHook'

const usePosPayment = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { isCashDrawerOpened } = useAppSelector((state) => state.posTerminal)
    const currentClient = useAppSelector((state) => state.client.currentClient)
    const { isMobile } = useScreenSize()

    const paymentSettingWarning = useCallback(() => {
        dispatch(
            showErrorMessage({
                message: t('quick_sell.message.payment_setting_warning'),
            }),
        )
    }, [dispatch, t])
    const cashDrawerNeedToOpenWarning = useCallback(() => {
        dispatch(
            showErrorMessage({
                message: t('quick_sell.message.cash_drawer_not_open_warning'),
            }),
        )
    }, [dispatch, t])
    const promptyPayPaymentSettingWarning = useCallback(() => {
        dispatch(
            showErrorMessage({
                message: t('quick_sell.message.prompt_pay_warning'),
            }),
        )
    }, [dispatch, t])
    //disable promptpay payment if account prompt pay do not setting
    const validationPromptPaySetting = useCallback(
        (promptpaySetting: PaymentSettingContract) => {
            if (promptpaySetting) {
                if (
                    !promptpaySetting.accountPromptPayID &&
                    promptpaySetting.isAvailableInPOS
                ) {
                    promptyPayPaymentSettingWarning()
                    return false
                }
            }
            return promptpaySetting?.isAvailableInPOS ?? false
        },
        [promptyPayPaymentSettingWarning],
    )
    //disable cash if it use cash drawer and not open the cash terminal
    const validationCashPaymentSetting = useCallback(
        (cashSetting: PaymentSettingContract) => {
            if (
                !isUndefined(currentClient) &&
                !!currentClient?.posTerminalIsUseCashDrawer &&
                !isNil(isCashDrawerOpened) &&
                !isCashDrawerOpened
            ) {
                cashDrawerNeedToOpenWarning()
                return false
            }
            return cashSetting.isAvailableInPOS
        },
        [cashDrawerNeedToOpenWarning, currentClient, isCashDrawerOpened],
    )
    const validationPaymentSetting = useCallback(
        (payments: PaymentSettingContract[]) => {
            if (
                (payments?.length ?? 0) === 0 ||
                !payments.some((a) => a.isAvailableInPOS)
            ) {
                paymentSettingWarning()
                return false
            } else {
                const cashSetting = payments.find(
                    (a) =>
                        a.paymentTypeID === PAYMENT_TYPE_ID.CASH &&
                        !a.customPaymentTypeID,
                )
                if (cashSetting && cashSetting.isAvailableInPOS) {
                    if (!validationCashPaymentSetting(cashSetting)) {
                        cashSetting.isAvailableInPOS = false
                        if (payments.length === 1) return false
                        else return true
                    }
                }
            }
            return true
        },
        [paymentSettingWarning, validationCashPaymentSetting],
    )

    const getPosPaymentAvailable = useCallback(
        (paymentSettings: PaymentSettingContract[]) => {
            let posPayments = [
                ...cloneDeep(paymentSettings)
                    .filter(
                        (a) =>
                            a.paymentTypeID !== PAYMENT_TYPE_ID.CHANNEL_ACCOUNT,
                    )
                    .filter(
                        (a) =>
                            !isMobile ||
                            //allow to make payment in mobile
                            [
                                PAYMENT_TYPE_ID.CASH,
                                PAYMENT_TYPE_ID.EVO_PAYMENT,
                                PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT,
                                PAYMENT_TYPE_ID.CREDITCARD,
                            ].includes(a!.paymentTypeID!),
                    ),
            ]

            const ppSetting = posPayments.find(
                (a) =>
                    a.paymentTypeID === PAYMENT_TYPE_ID.PROMPTPAY &&
                    !a.customPaymentTypeID,
            )
            if (ppSetting)
                ppSetting.isAvailableInPOS = validationPromptPaySetting(
                    ppSetting,
                )
            return sortBy(
                posPayments.filter((a) => a.isAvailableInPOS),
                (a) => a.sortNumber,
            )
        },
        [isMobile, validationPromptPaySetting],
    )

    return {
        validationPaymentSetting,
        getPosPaymentAvailable,
    }
}
export default usePosPayment
